/* ---------------------------------------------------------------------------
   The contract a programme is administered against (LC-42).

   A baseline says what was planned; the contract says what was promised. The
   two meet in one date — the time for completion — and a period report that
   printed a forecast without it would leave the reader to find the contract to
   see whether the forecast mattered.

   THE HEADER TRAVELS WITH THE BASELINE. Baselines are append-only, so a
   revision carries the header forward as it stood when the revision was taken,
   and the report for a month reads the header of the baseline it measured
   against. EXTENSIONS OF TIME DO NOT: an extension is granted to the contract,
   on a day, by an approver, and it moves the completion date of every baseline
   from then on. They are their own rows (`contract_extensions`, migration 0041).

   COMMENCEMENT IS REQUIRED, and it is the programme's day zero. Before this item
   a baseline taken from a model with no project start was frozen from today,
   which made every planned date a statement about the day somebody pressed a
   button. Now the register refuses instead.

   Pure, shared with functions/lib/baselines.ts and functions/lib/contracts.ts.
   --------------------------------------------------------------------------- */

import { addDays, isIsoDay } from './periods.ts';

export interface ContractMilestone {
  name: string;
  dueOn: string;
}

export interface ContractHeader {
  contractor: string;
  contractNo: string;
  /** In `currency`, the department's own (TenantProfile.currency). Null where not stated. */
  value: number | null;
  currency: string;
  /** The commencement date — the programme's day zero. */
  commencement: string;
  /** Time for completion, in calendar days, as the contract states it. */
  completionDays: number;
  milestones: ContractMilestone[];
}

export type ExtensionState = 'proposed' | 'accepted' | 'rejected';

export const EXTENSION_STATE_LABEL: Record<ExtensionState, string> = {
  proposed: 'Proposed',
  accepted: 'Accepted',
  rejected: 'Rejected',
};

/** An extension of time, as granted — or not — by the approver. */
export interface ContractExtension {
  id: string;
  bridgeId: string;
  reason: string;
  days: number;
  state: ExtensionState;
  raisedBy: string | null;
  raisedAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
}

/**
 * The last day of the time for completion: commencement plus the days, less
 * one, because the commencement date is the first of them. Accepted extensions
 * add their days; proposed and rejected ones add nothing.
 */
export function completionDate(
  c: Pick<ContractHeader, 'commencement' | 'completionDays'>,
  extensions: readonly Pick<ContractExtension, 'state' | 'days'>[] = [],
): string {
  const granted = extensions.filter((e) => e.state === 'accepted').reduce((sum, e) => sum + e.days, 0);
  return addDays(c.commencement, Math.max(0, c.completionDays + granted - 1));
}

/** Days granted by accepted extensions. */
export function grantedDays(extensions: readonly Pick<ContractExtension, 'state' | 'days'>[]): number {
  return extensions.filter((e) => e.state === 'accepted').reduce((sum, e) => sum + e.days, 0);
}

/**
 * Milestones from the form's text, one per line: `2026-08-15 Substructure
 * complete`. Lines that do not parse come back in `bad`.
 */
export function parseMilestones(text: string): { milestones: ContractMilestone[]; bad: string[] } {
  const milestones: ContractMilestone[] = [];
  const bad: string[] = [];
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (line === '') continue;
    const m = /^(\d{4}-\d{2}-\d{2})\s+(.+)$/.exec(line);
    if (m && isIsoDay(m[1]!)) milestones.push({ dueOn: m[1]!, name: m[2]!.trim() });
    else bad.push(line);
  }
  return { milestones, bad };
}

export function milestoneLines(milestones: readonly ContractMilestone[]): string {
  return milestones.map((m) => `${m.dueOn} ${m.name}`).join('\n');
}

/**
 * A header, checked — or the sentence saying what is missing. The same rule on
 * the form and on the server.
 */
export function checkContract(c: ContractHeader): string | null {
  if (!isIsoDay(c.commencement)) {
    return 'The contract needs its commencement date: it is the programme’s day zero, and a baseline is not frozen from today.';
  }
  if (!Number.isInteger(c.completionDays) || c.completionDays < 1 || c.completionDays > 3660 * 3) {
    return 'The time for completion must be a whole number of days.';
  }
  if (c.value !== null && (!Number.isFinite(c.value) || c.value < 0)) {
    return 'The contract value must be a number, or left blank.';
  }
  for (const m of c.milestones) {
    if (!isIsoDay(m.dueOn) || m.name.trim() === '') return 'Each milestone needs a date and a name.';
  }
  if (c.milestones.length > 60) return 'That is more milestones than a contract header holds.';
  return null;
}
